import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, InputAdornment, Snackbar, SnackbarContent, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../../core/authContext';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { DEFAULT_ROWS_PER_PAGE, type RowsPerPage } from '../../components/RowsPerPageSelect';
import { fetchDataStatus, type PeDataStatus } from '../ProgramEvaluation/dataStatus';
import { fetchSetupState, settingsConfirmed, type SetupState } from '../../core/setupStateApi';
import { GeneratePlansDialog } from '../Generate/GeneratePlansDialog';
import { APP_LINK_STATE, NAV_ROUTES } from '../Home/navRoutes';
import {
  classificationResultOf,
  fetchRosterClassificationResult,
  type RosterClassificationResult,
  type RosterClassificationRow,
  fetchPlanSignOffs,
  type PlanSignOffsResult,
} from './rosterClassification';
import { formatUploadDate, rolledOver } from './lastUpdated';
import { rosterAsOfLabel, type RosterMeta } from '../../core/rosterMeta';
import { logEvent } from '../../utils/eventLog';
import {
  classificationToRosterRow,
  filterRows,
  filterRowsBySchool,
  pageRows,
  sortRowsByName,
  type RosterRow,
} from './rosterRows';
import { RosterTable } from './RosterTable';
import { FilterBar } from './FilterBar';
import { DimensionPicker } from './DimensionPicker';
import { applyFilters, type FilterChip } from './filters';
import { enumerateValues } from './dimensions';
import { withSuggestion } from './filterSuggestions';
import { evidenceColumnsFor } from './evidenceColumns';
import { departedEmptyState, scopeByEnrollment } from './enrollmentScope';
import { rosterCountLine } from './countLine';
import { LoadFailure } from '../../components/LoadFailure';
import { TableLoading } from '../../components/TableLoading';
import { usePlansStatus } from '../Plans/plansGenerated';
import { SAMPLE_PLAN_ROUTE } from '../Samples/routes';
import { ROSTER } from './tokens';
import { StudentsBanner } from './StudentsBanner';
import { accessStudentCount, studentsBannerCopy } from './bannerState';
import { PAGE_MAX_WIDTH } from '../../theme';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

const TOAST_BG = '#2C2C52';

type Props = {
  classificationSource?: () => Promise<RosterClassificationRow[] | RosterClassificationResult | null>;
  dataStatusSource?: () => Promise<PeDataStatus>;
  setupStateSource?: () => Promise<SetupState>;
  initialChips?: FilterChip[];
  planSignOffsSource?: () => Promise<PlanSignOffsResult | null>;
};

function EmptyState({ onGoToData }: { onGoToData?: () => void }) {
  return (
    <Box sx={{ px: '16px', pb: '24px', textAlign: 'center' }}>
      <Typography
        sx={{ fontFamily: ROSTER.font, fontSize: 16, lineHeight: '24px', color: ROSTER.muted }}
      >
        No students yet. Upload your district’s data to see your EL roster.
      </Typography>
      {onGoToData && (
        <Button
          onClick={onGoToData}
          sx={{ mt: 2, textTransform: 'none', fontFamily: ROSTER.font, fontWeight: 600 }}
        >
          Go to Data & Integrations
        </Button>
      )}
    </Box>
  );
}

function signOffReason(chip: FilterChip | undefined, signOffs: PlanSignOffsResult | null | undefined): string | null {
  if (chip === undefined || !('values' in chip) || !signOffs) return null;
  if (!signOffs.generated && Object.keys(signOffs.students).length === 0) {
    return `Plans for ${signOffs.year} haven’t been generated yet, so no student has a sign-off status.`;
  }
  const asked = enumerateValues('planSignoff', [])
    .filter((option) => chip.values.includes(option.value))
    .map((option) => option.label.toLowerCase())
    .join(' or ');
  return `No ${signOffs.year} plan you can see is ${asked}.`;
}

function NoMatches({ reason }: { reason?: string | null }) {
  return (
    <Box sx={{ px: '16px', pb: '24px', textAlign: 'center' }}>
      <Typography
        sx={{ fontFamily: ROSTER.font, fontSize: 16, lineHeight: '24px', color: ROSTER.ink }}
      >
        No students match these filters.
      </Typography>
      <Typography
        sx={{ fontFamily: ROSTER.font, fontSize: 13, lineHeight: '20px', color: ROSTER.muted }}
      >
        {reason ?? 'Remove a chip or adjust a value — the filters stay put.'}
      </Typography>
    </Box>
  );
}

export function RosterPage({
  classificationSource = fetchRosterClassificationResult,
  dataStatusSource = fetchDataStatus,
  setupStateSource = fetchSetupState,
  initialChips = [],
  planSignOffsSource = fetchPlanSignOffs,
}: Props = {}) {
  const navigate = useNavigate();
  const { isSuperAdmin, isDistrictAdmin } = useAuth();
  const admin = isSuperAdmin || isDistrictAdmin;
  const [searchParams] = useSearchParams();
  const schoolFilter = searchParams.get('school') ?? '';
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPage>(DEFAULT_ROWS_PER_PAGE);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [rosterMeta, setRosterMeta] = useState<RosterMeta | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [failed, setFailed] = useState(false);
  const [rows, setRows] = useState<RosterRow[]>([]);
  const [dataStatus, setDataStatus] = useState<PeDataStatus | null | undefined>(undefined);
  const [setupState, setSetupState] = useState<SetupState | null | undefined>(undefined);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const plans = usePlansStatus();
  const accessInRows = rows.some((row) => row.noPlan !== true);
  const accessUploaded: boolean | null = !admin
    ? hydrated && !failed
      ? accessInRows
      : null
    : dataStatus === undefined
      ? null
      : dataStatus === null
        ? hydrated && !failed
          ? accessInRows
          : null
        : dataStatus.accessPresent || accessInRows;
  const banner =
    hydrated && !failed && plans !== null && accessUploaded !== null
      ? studentsBannerCopy({
          admin,
          accessUploaded,
          settingsConfirmed: setupState == null ? null : settingsConfirmed(setupState),
          plansGenerated: plans.generated,
          accessStudentCount: accessStudentCount(rows),
        })
      : null;
  const openStudent = (id: string) => navigate(`/roster/${encodeURIComponent(id)}`);
  const [chips, setChips] = useState<FilterChip[]>(initialChips);
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
  const [editing, setEditing] = useState<FilterChip | null>(null);
  const [includeDeparted, setIncludeDeparted] = useState(false);

  useEffect(() => {
    if (!admin) return undefined;
    let active = true;
    dataStatusSource()
      .then((status) => {
        if (active) setDataStatus(status);
      })
      .catch(() => {
        if (active) setDataStatus(null);
      });
    setupStateSource()
      .then((state) => {
        if (active) setSetupState(state);
      })
      .catch(() => {
        if (active) setSetupState(null);
      });
    return () => {
      active = false;
    };
  }, [admin, dataStatusSource, setupStateSource]);

  // Every row here is the server's answer about a student, from the same
  // assembly the report is generated from. Nothing on this page parses a CSV.
  useEffect(() => {
    let active = true;
    classificationSource()
      .catch(() => null)
      .then((answer) => {
        if (!active) return;
        const classified = classificationResultOf(answer);
        setFailed(classified === null);
        setRows((classified?.students ?? []).map(classificationToRosterRow));
        setRosterMeta(classified?.roster ?? null);
        setLastUpdated(classified?.lastUploadedAt ?? null);
        setHydrated(true);
      });
    return () => {
      active = false;
    };
  }, [classificationSource]);

  // Timed once the hydrated table has committed, so the wait a person saw
  // includes the render, not only the fetch.
  const loadStartedAt = useRef<number | null>(null);
  useEffect(() => {
    if (!hydrated) {
      if (loadStartedAt.current === null) loadStartedAt.current = performance.now();
      return;
    }
    if (loadStartedAt.current === null) return;
    const durationMs = String(Math.max(0, Math.round(performance.now() - loadStartedAt.current)));
    loadStartedAt.current = null;
    logEvent('page.loaded.v1', { page: 'roster', durationMs, status: failed ? 'error' : 'ok' }).catch(() => {});
  }, [hydrated, failed]);

  const schoolRows = useMemo(
    () => filterRowsBySchool(rows, schoolFilter),
    [rows, schoolFilter],
  );

  // A School column that repeats one name in every row says nothing, so it
  // only shows when the viewer's whole roster spans more than one school.
  const severalSchools = useMemo(
    () => new Set(rows.map((row) => row.school.trim().toLowerCase())).size > 1,
    [rows],
  );

  const signOffChip = chips.find((chip) => chip.dimension === 'planSignoff');
  const plansGenerated = plans?.generated === true;
  const [generationsConfirmed, setGenerationsConfirmed] = useState(0);
  const [signOffsAsked, setSignOffsAsked] = useState<
    { generated: boolean; confirmed: number; result: PlanSignOffsResult | null } | undefined
  >(undefined);
  const signOffs = signOffsAsked?.result;
  useEffect(() => {
    if (!admin || signOffChip === undefined || dialogOpen) return undefined;
    if (
      signOffsAsked !== undefined &&
      signOffsAsked.generated === plansGenerated &&
      signOffsAsked.confirmed === generationsConfirmed
    ) {
      return undefined;
    }
    let active = true;
    planSignOffsSource()
      .catch(() => null)
      .then((result) => {
        if (active) setSignOffsAsked({ generated: plansGenerated, confirmed: generationsConfirmed, result });
      });
    return () => {
      active = false;
    };
  }, [admin, signOffChip, dialogOpen, signOffsAsked, plansGenerated, generationsConfirmed, planSignOffsSource]);
  const signedRows = useMemo(
    () =>
      signOffs == null
        ? schoolRows
        : schoolRows.map((row) => ({ ...row, planSignOff: signOffs.students[row.id] ?? null })),
    [schoolRows, signOffs],
  );

  const scopedRows = useMemo(
    () => scopeByEnrollment(signedRows, includeDeparted),
    [signedRows, includeDeparted],
  );

  const visibleRows = useMemo(
    () =>
      sortRowsByName(
        applyFilters(
          filterRows(scopedRows, query),
          signOffs == null ? chips.filter((chip) => chip.dimension !== 'planSignoff') : chips,
        ),
        sortDirection,
      ),
    [scopedRows, query, chips, signOffs, sortDirection],
  );
  const allDeparted = useMemo(
    () => departedEmptyState(schoolRows, includeDeparted),
    [schoolRows, includeDeparted],
  );

  const totalPages = Math.max(1, Math.ceil(visibleRows.length / rowsPerPage));

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: ROSTER.surface }}>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="roster" />
      <Box component="main" sx={{ flex: 1, minWidth: 0, p: '32px 24px' }}>
        <Box sx={{ maxWidth: PAGE_MAX_WIDTH, mx: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', minWidth: 0, flexWrap: 'wrap' }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: ROSTER.font,
                fontSize: 28,
                fontWeight: 600,
                lineHeight: '36px',
                color: ROSTER.ink,
              }}
            >
              Students
            </Typography>
            {hydrated && (
              <Typography
                data-testid="roster-count-line"
                sx={{
                  fontFamily: ROSTER.font,
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: '16px',
                  color: ROSTER.muted,
                }}
              >
                {rosterCountLine({
                  shown: visibleRows.length,
                  total: scopedRows.length,
                  filtered: query.trim() !== '' || chips.length > 0,
                  lastUpdated: lastUpdated == null ? null : formatUploadDate(lastUpdated),
                  rosterNote: rosterAsOfLabel(rosterMeta),
                  rolledOver: lastUpdated != null && rolledOver(lastUpdated, new Date()),
                })}
              </Typography>
            )}
          </Box>
          {banner && (
            <StudentsBanner
              copy={banner}
              onSeeSample={() => navigate(SAMPLE_PLAN_ROUTE, { state: { from: 'roster' } })}
              onUploadAccess={admin ? () => navigate(NAV_ROUTES.data) : undefined}
              onGoToDashboard={() => navigate(NAV_ROUTES.dashboard, { state: APP_LINK_STATE })}
              onGeneratePlans={() => setDialogOpen(true)}
            />
          )}
          <Box
            data-testid="roster-card"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              pt: '16px',
              bgcolor: ROSTER.cardBg,
              borderRadius: '12px',
              boxShadow: ROSTER.cardShadow,
            }}
          >
            <Box
              data-testid="roster-controls"
              sx={{ display: 'flex', alignItems: 'center', gap: '8px', px: '16px', flexWrap: 'wrap' }}
            >
              {rows.length > 0 && (
                <FilterBar
                  chips={chips}
                  onRemove={(index) => {
                    if (chips[index]?.dimension === 'planSignoff') setSignOffsAsked(undefined);
                    setChips((current) => current.filter((_, i) => i !== index));
                    setPage(1);
                  }}
                  onClearAll={() => {
                    setSignOffsAsked(undefined);
                    setChips([]);
                    setPage(1);
                  }}
                  onAdd={(anchor) => {
                    setEditing(null);
                    setPickerAnchor(anchor);
                  }}
                  onEdit={(index, anchor) => {
                    setEditing(chips[index] ?? null);
                    setPickerAnchor(anchor);
                  }}
                />
              )}
              <TextField
                size="small"
                placeholder="Search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: ROSTER.muted, width: 24, height: 24 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: '1 1 0',
                  minWidth: 160,
                  '& .MuiOutlinedInput-root': { height: 36, borderRadius: '8px', fontFamily: ROSTER.font, fontSize: 16 },
                  '& input::placeholder': { textOverflow: 'ellipsis' },
                }}
              />
            </Box>
            {signOffChip !== undefined && signOffs === null && (
              <Typography
                data-testid="sign-offs-unavailable"
                sx={{ px: '16px', pb: '8px', fontFamily: ROSTER.font, fontSize: 13, lineHeight: '20px', color: ROSTER.muted }}
              >
                Plan sign-off statuses couldn’t be loaded, so that filter is not applied.
              </Typography>
            )}
            {rows.length > 0 && (
              <Box sx={{ px: '16px' }}>
                <Box
                  component="label"
                  data-testid="include-departed"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: 'fit-content',
                    fontFamily: ROSTER.font,
                    fontSize: 13,
                    color: ROSTER.ink,
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    component="input"
                    type="checkbox"
                    checked={includeDeparted}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setIncludeDeparted(e.target.checked);
                      setPage(1);
                    }}
                  />
                  Include departed students
                </Box>
                <DimensionPicker
                  anchorEl={pickerAnchor}
                  open={pickerAnchor !== null}
                  admin={admin}
                  rows={signedRows}
                  editing={editing}
                  chips={chips}
                  onClose={() => {
                    setEditing(null);
                    setPickerAnchor(null);
                  }}
                  onPickDimension={() => {}}
                  onPickSuggestion={(suggestion) => {
                    setChips((current) => withSuggestion(current, suggestion));
                    setPage(1);
                    setPickerAnchor(null);
                  }}
                  onPickStudent={(id) => {
                    setPickerAnchor(null);
                    openStudent(id);
                  }}
                  onApply={(chip) => {
                    const sameSlot = (existing: FilterChip) =>
                      existing.dimension === chip.dimension &&
                      (existing as { domain?: string }).domain ===
                        (chip as { domain?: string }).domain;
                    setChips((current) => [...current.filter((e) => !sameSlot(e)), chip]);
                    setEditing(null);
                    setPage(1);
                    setPickerAnchor(null);
                  }}
                />
              </Box>
            )}
            {rows.length === 0 ? (
              !hydrated ? (
                <TableLoading tokens={ROSTER} />
              ) : failed ? (
                <LoadFailure tokens={ROSTER} />
              ) : (
                <EmptyState onGoToData={admin ? () => navigate('/data') : undefined} />
              )
            ) : allDeparted ? (
              <Box sx={{ px: '16px', pb: '24px', textAlign: 'center' }}>
                <Typography
                  data-testid="roster-all-departed"
                  sx={{ fontFamily: ROSTER.font, fontSize: 16, lineHeight: '24px', color: ROSTER.ink }}
                >
                  No active students in your most recent dataset.
                </Typography>
                <Typography
                  sx={{ fontFamily: ROSTER.font, fontSize: 13, lineHeight: '20px', color: ROSTER.muted }}
                >
                  Turn on “Include departed students” to see who was here before.
                </Typography>
              </Box>
            ) : visibleRows.length === 0 && chips.length > 0 ? (
              <NoMatches reason={signOffReason(signOffChip, signOffs)} />
            ) : (
              <RosterTable
                evidenceColumns={evidenceColumnsFor(chips)}
                rows={pageRows(visibleRows, page, rowsPerPage)}
                sortDirection={sortDirection}
                onToggleSort={() =>
                  setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
                }
                onOpenRow={openStudent}
                accessUploaded={accessUploaded === true}
                showSchool={severalSchools}
                page={page}
                totalRows={visibleRows.length}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(next) => {
                  setRowsPerPage(next);
                  setPage(1);
                }}
                onPrevPage={() => setPage((p) => Math.max(1, p - 1))}
                onNextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
              />
            )}
          </Box>
        </Box>
      </Box>
      {admin && (
        <GeneratePlansDialog
          open={dialogOpen}
          studentCount={accessStudentCount(rows)}
          onClose={() => setDialogOpen(false)}
          onGenerated={(count) => {
            setDialogOpen(false);
            setGenerationsConfirmed((confirmed) => confirmed + 1);
            setToast(`Plans generated for ${count} students.`);
          }}
        />
      )}
      {toast && (
        <Snackbar
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          autoHideDuration={8000}
          onClose={(_, reason) => reason !== 'clickaway' && setToast(null)}
        >
          <SnackbarContent
            role="status"
            data-testid="plans-generated-toast"
            message={toast}
            sx={{ bgcolor: TOAST_BG, color: '#FFFFFF', borderRadius: '4px' }}
          />
        </Snackbar>
      )}
    </Box>
  );
}
